import { useEffect } from "react";
import { matchPath, useLocation } from "react-router";

export interface RouteTitle {
  /** Path pattern in react-router syntax (e.g. "/:tenantSlug/dashboard/:slug"). */
  path: string;
  /** Human title; empty string skips writing (e.g. transient redirect routes). */
  title: string;
}

export interface DocumentTitleEffectProps {
  /**
   * Ordered list of path patterns + titles. The first pattern that matches the
   * current `location.pathname` wins, so list more-specific patterns first
   * (e.g. `/login` before `/:tenantSlug`).
   */
  routeTitles: ReadonlyArray<RouteTitle>;
  /** Appended after the matched title (e.g. " | SimplySpark"). */
  suffix: string;
  /** Title used when no pattern matches the current location. */
  fallback: string;
}

/**
 * Writes `document.title` from the active route's static title on every navigation. Per-page
 * `useDocumentTitle(name)` calls run after this and override with the hydrated entity name. The
 * early-exit guard keeps this a no-op when the title hasn't actually changed. Implements WCAG 2.4.2
 * (Page Titled) for SIM-1398.
 */
export function DocumentTitleEffect({ routeTitles, suffix, fallback }: DocumentTitleEffectProps) {
  const location = useLocation();
  useEffect(() => {
    const match = routeTitles.find((r) =>
      matchPath({ path: r.path, end: true }, location.pathname),
    );
    const baseTitle = match?.title ?? "";
    const next = baseTitle ? `${baseTitle}${suffix}` : fallback;
    if (typeof document === "undefined") return;
    if (document.title !== next) document.title = next;
  }, [routeTitles, location.pathname, suffix, fallback]);
  return null;
}
